// Prefetches the publisher experience for a mapped page, keyed `url:<page>`
// in the registry popover.js shares.
import { warmDefaultPlayIcons } from "@/modules/assetImageCache";
import type { PrefetchEntry } from "@/types/globals";

// Mirrors `experiencePayloadUtils.normalizeExperienceBody` from the
// player bundle — handles both wrapped (`{ data: { video } }`) and
// flat payload shapes so we don't double-cache an outer wrapper.
function normalizeExperienceBodyVanilla(
  raw: unknown,
): { idexperience?: unknown; [key: string]: unknown } | null {
  type Raw = { data?: { video?: unknown }; idexperience?: unknown };
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const body = raw as Raw;
  if (
    body.data !== undefined &&
    typeof body.data === "object" &&
    body.data.video !== undefined
  ) {
    return body.data;
  }
  return body;
}

export function isLocalPlayHost(host: string): boolean {
  if (!host) {
    return false;
  }
  const h = String(host).toLowerCase();
  return (
    h.indexOf("localhost") >= 0 ||
    h.indexOf("127.0.0.1") >= 0 ||
    h.endsWith("play.pixmoto.loc")
  );
}

function ensurePixmotoBucket() {
  window.__pixmoto = window.__pixmoto || {};
  if (!window.__pixmoto.prefetchedExperiences) {
    window.__pixmoto.prefetchedExperiences = {};
  }
  if (!window.__pixmoto._prefetchInflight) {
    window.__pixmoto._prefetchInflight = {};
  }
  warmDefaultPlayIcons();
  return window.__pixmoto as Required<
    Pick<
      NonNullable<typeof window.__pixmoto>,
      "prefetchedExperiences" | "_prefetchInflight"
    >
  > &
    NonNullable<typeof window.__pixmoto>;
}

// Warm the publisher experience API + image assets for this page so
// the player iframe can read from cache instead of hitting the
// network. Uses `window.__pixmoto.prefetchedExperiences` + an
// inflight registry shared with `popover.js` (see
// `popover/anchorPrefetch.ts#runPrefetchForAnchor`) so the two paths don't double-
// fetch when both run.
export function prefetchExperienceData(
  pageUrl: string,
  getExperienceByUrlApiUrl: (pageUrl: string) => string,
) {
  if (!pageUrl) {
    return;
  }
  const pm = ensurePixmotoBucket();
  const cacheKey = `url:${pageUrl}`;
  const existing = pm.prefetchedExperiences[cacheKey];
  if (existing && existing.status === "ok") {
    // JSON already prefetched; the injected popover anchor warms the
    // video on hover/tap (popover/anchorSetup.ts armWarmOnIntent).
    return;
  }
  if (pm._prefetchInflight[cacheKey]) {
    return;
  }

  const apiUrl = getExperienceByUrlApiUrl(pageUrl);
  pm._prefetchInflight[cacheKey] = true;

  fetch(apiUrl, {
    method: "GET",
    credentials: "omit",
    mode: "cors",
  })
    .then(res =>
      res.ok
        ? res.json().then(
            json => ({ ok: true, json }),
            () => ({ ok: false, json: null }),
          )
        : { ok: false, json: null },
    )
    .then(result => {
      delete pm._prefetchInflight[cacheKey];
      if (!result.ok) {
        pm.prefetchedExperiences[cacheKey] = {
          status: "error",
          fetchedAt: Date.now(),
        };
        return;
      }
      const entry: PrefetchEntry = {
        status: "ok",
        data: result.json,
        fetchedAt: Date.now(),
      };
      pm.prefetchedExperiences[cacheKey] = entry;

      const body = normalizeExperienceBodyVanilla(result.json);
      if (body?.idexperience != null) {
        const idKey = String(body.idexperience);
        pm.prefetchedExperiences[idKey] = entry;
      }
      // Video warm is intentionally NOT started here — the injected
      // popover anchor's intent-gating (popover/anchorSetup.ts armWarmOnIntent)
      // warms on hover/tap.

      // If overlay opened with `pix-embed-prefetch=1` while
      // we were inflight, the iframe is waiting for the
      // parent to post the payload — popover.js exposes
      // this hook for exactly that hand-off.
      if (
        typeof pm.popover?._postPrefetchToIframeWindowExternal === "function"
      ) {
        try {
          pm.popover._postPrefetchToIframeWindowExternal();
        } catch (_e) {
          /* ignore */
        }
      }
    })
    .catch(() => {
      delete pm._prefetchInflight[cacheKey];
      pm.prefetchedExperiences[cacheKey] = {
        status: "error",
        fetchedAt: Date.now(),
      };
    });
}
